
"use client";

import React, { useState } from "react";

const CssBoxShadowGenerate = () => {
  const [xOffset, setXOffset] = useState(21);
  const [yOffset, setYOffset] = useState(5);
  const [blur, setBlur] = useState(15);
  const [spread, setSpread] = useState(0);
  const [color, setColor] = useState("#000000");
  const [opacity, setOpacity] = useState(20);
  const [inset, setInset] = useState(false);
  const [borderRadius, setBorderRadius] = useState(8);
  const [copied, setCopied] = useState(false);

  const shadowColor = `rgba(0, 0, 0, ${opacity / 100})`;

  const boxShadow = `${inset ? "inset " : ""}${xOffset}px ${yOffset}px ${blur}px ${spread}px ${hexToRgba(color, opacity)}`;

  function hexToRgba(hex, alpha) {
    const normalized = hex.replace("#", "");
    const fullHex =
      normalized.length === 3
        ? normalized.split("").map((char) => char + char).join("")
        : normalized;

    const value = parseInt(fullHex, 16);
    const r = (value >> 16) & 255;
    const g = (value >> 8) & 255;
    const b = value & 255;

    return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
  }

  const cssCode = `box-shadow: ${boxShadow};\nborder-radius: ${borderRadius}px;`;

  const presets = {
    Soft: {
      xOffset: 0,
      yOffset: 4,
      blur: 15,
      spread: 0,
      opacity: 15,
      inset: false,
    },
    Hard: {
      xOffset: 8,
      yOffset: 8,
      blur: 0,
      spread: 0,
      opacity: 40,
      inset: false,
    },
    Elevated: {
      xOffset: 0,
      yOffset: 10,
      blur: 25,
      spread: -5,
      opacity: 25,
      inset: false,
    },
    Inset: {
      xOffset: 0,
      yOffset: 4,
      blur: 10,
      spread: 0,
      opacity: 25,
      inset: true,
    },
    Layered: {
      xOffset: 0,
      yOffset: 5,
      blur: 10,
      spread: 0,
      opacity: 20,
      inset: false,
    },
    None: {
      xOffset: 0,
      yOffset: 0,
      blur: 0,
      spread: 0,
      opacity: 0,
      inset: false,
    },
  };

  const applyPreset = (preset) => {
    const settings = presets[preset];
    setXOffset(settings.xOffset);
    setYOffset(settings.yOffset);
    setBlur(settings.blur);
    setSpread(settings.spread);
    setOpacity(settings.opacity);
    setInset(settings.inset);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cssCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = cssCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const Slider = ({
    label,
    value,
    min,
    max,
    onChange,
    unit = "px",
  }) => (
    <div className="shadow-control">
      <label>{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="control-value">
        {value}{unit}
      </span>
    </div>
  );

  return (
    <div className="shadow-generator container every_page_mg_top">
      <style>{`
        .shadow-generator {
          width: 100%;
          padding: 14px;
          box-sizing: border-box;
          color: #111;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 12px;
          background: #fff;
        }

        .shadow-generator * {
          box-sizing: border-box;
        }

        .shadow-preview {
          width: 100%;
          height: 225px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: #f0f0f0;
          border: 1px solid #dedede;
          border-radius: 6px;
          position: relative;
          isolation: isolate;
        }

        .shadow-preview::before {
          content: "";
          position: absolute;
          width: 180px;
          height: 100px;
          top: -40px;
          right: 20px;
          background: #d9eee4;
          opacity: 0.35;
          transform: rotate(35deg);
          z-index: -1;
        }

        .shadow-box {
          width: 150px;
          height: 150px;
          flex-shrink: 0;
          background: #fff;
          transition: box-shadow 0.15s ease, border-radius 0.15s ease;
        }

        .shadow-controls {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
          gap: 22px;
          margin-top: 12px;
        }

        .shadow-control {
          min-height: 24px;
          display: grid;
          grid-template-columns: 74px minmax(50px, 1fr) 36px;
          align-items: center;
          gap: 10px;
          margin-bottom: 0;
        }

        .shadow-control label {
          font-weight: 600;
          white-space: nowrap;
        }

        .shadow-control input[type="range"] {
          width: 100%;
          min-width: 0;
          margin: 0;
          cursor: pointer;
          accent-color: #3970b0;
        }

        .control-value {
          font-size: 10px;
          text-align: right;
          white-space: nowrap;
        }

        .color-control {
          display: grid;
          grid-template-columns: 74px 36px 1fr;
          align-items: center;
          gap: 10px;
          margin-bottom: 5px;
          min-height: 24px;
        }

        .color-control label,
        .inset-control label {
          font-weight: 600;
        }

        .color-control input[type="color"] {
          width: 36px;
          height: 23px;
          padding: 2px;
          border: 1px solid #ddd;
          background: white;
          cursor: pointer;
        }

        .inset-control {
          display: flex;
          align-items: center;
          gap: 5px;
          min-height: 24px;
        }

        .inset-control input {
          margin: 0;
          accent-color: #4479b6;
          cursor: pointer;
        }

        .shadow-presets {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin: 22px 0 18px;
        }

        .preset-label {
          white-space: nowrap;
        }

        .preset-button {
          padding: 7px 15px;
          background: #6674cc;
          color: white;
          border: none;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
        }

        .preset-button:hover {
          background: #6674cc;
          transform: translateY(-1px);
        }

        .css-heading {
          font-size: 12px;
          margin: 0 0 7px;
          font-weight: 500;
        }

        .css-output {
          position: relative;
          padding: 12px;
          padding-right: 55px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 5px;
          overflow-wrap: anywhere;
        }

        .css-output pre {
          margin: 0;
          font-family: Consolas, Monaco, monospace;
          font-size: 11px;
          line-height: 1.7;
          white-space: pre-wrap;
        }

        .copy-button {
          position: absolute;
          top: 6px;
          right: 6px;
          padding: 4px 7px;
          color: #fff;
          background: #888;
          border: none;
          border-radius: 3px;
          font-size: 10px;
          cursor: pointer;
        }

        .copy-button:hover {
          background: #555;
        }

        @media (max-width: 650px) {
          .shadow-generator {
            padding: 10px;
          }

          .shadow-preview {
            height: 200px;
          }

          .shadow-box {
            width: 120px;
            height: 120px;
          }

          .shadow-controls {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .shadow-control {
            grid-template-columns: 85px minmax(40px, 1fr) 38px;
            gap: 8px;
          }

          .color-control {
            grid-template-columns: 85px 36px 1fr;
          }

          .shadow-presets {
            gap: 8px;
            margin-top: 18px;
          }

          .preset-button {
            padding: 8px 11px;
          }
        }
      `}</style>

      {/* Live Preview */}
      <div className="shadow-preview">
        <div
          className="shadow-box"
          style={{
            boxShadow,
            borderRadius: `${borderRadius}px`,
          }}
        />
      </div>

      {/* Controls */}
      <div className="shadow-controls">
        <div>
          <Slider
            label="X Offset"
            value={xOffset}
            min={-50}
            max={50}
            onChange={setXOffset}
          />

          <Slider
            label="Y Offset"
            value={yOffset}
            min={-50}
            max={50}
            onChange={setYOffset}
          />

          <Slider
            label="Blur"
            value={blur}
            min={0}
            max={100}
            onChange={setBlur}
          />

          <Slider
            label="Spread"
            value={spread}
            min={-50}
            max={50}
            onChange={setSpread}
          />
        </div>

        <div>
          <div className="color-control">
            <label htmlFor="shadow-color">Color</label>
            <input
              id="shadow-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
            <span />
          </div>

          <Slider
            label="Opacity"
            value={opacity}
            min={0}
            max={100}
            unit="%"
            onChange={setOpacity}
          />

          <div className="inset-control">
            <label htmlFor="inset-shadow">
              Inset
            </label>
            <input
              id="inset-shadow"
              type="checkbox"
              checked={inset}
              onChange={(e) => setInset(e.target.checked)}
            />
            <label htmlFor="inset-shadow">
              Inset shadow
            </label>
          </div>

          <Slider
            label="Border Radius"
            value={borderRadius}
            min={0}
            max={100}
            onChange={setBorderRadius}
          />
        </div>
      </div>

      {/* Presets */}
      <div className="shadow-presets">
        <span className="preset-label">Presets:</span>

        {Object.keys(presets).map((preset) => (
          <button
            key={preset}
            type="button"
            className="preset-button"
            onClick={() => applyPreset(preset)}
          >
            {preset}
          </button>
        ))}
      </div>

      {/* CSS Output */}
      <h4 className="css-heading">CSS Code</h4>

      <div className="css-output">
        <pre>{cssCode}</pre>

        <button
          type="button"
          className="copy-button"
          onClick={handleCopy}
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
};

export default CssBoxShadowGenerate;
